import type { StaffOperationsIntent } from "@lafayette/schemas";
import { createColumnHelper } from "@tanstack/react-table";
import { TaskStatusBadge } from "../../operations/components/parts";
import { formatAge, shortId } from "../../operations/format";
import { errorClassLabels, stateLabels, taskLabels } from "../../operations/labels";
import type { useOperationsJobs, useOperationsSummary } from "../../operations/queries";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { DataTable } from "../../ui/data-table";
import { Icon } from "../../ui/icon";
import { wrapCells } from "../../ui/wrap-cells";
import type { DiagnosticsCapabilities } from "../access";
import { describeDiagnosticsError } from "../errors";
import { runbooks } from "../runbooks";
import type { DialogRequest } from "./dialog-state";
import { RecordLink } from "./RecordLink";
import { RunbookLink } from "./RunbookLink";
import { dash, hint, Section } from "./Section";

type FailedJobView = Readonly<{
  job: StaffOperationsIntent;
  onRerun: () => void;
}>;
const failedJobColumn = createColumnHelper<FailedJobView>();
// No `rowHeader` column here: many failed jobs share one task, so no cell of a row names that row alone (the id is in the button names).
const failedJobColumns = wrapCells([
  failedJobColumn.display({
    id: "task",
    header: "Task",

    cell: ({ row }) => (
      <>
        <span className="font-semibold">{taskLabels[row.original.job.taskId]}</span>
        <span className="text-xs font-normal text-muted-foreground">
          {" "}
          {stateLabels[row.original.job.state]}
        </span>
      </>
    ),
  }),
  failedJobColumn.display({
    id: "about",
    header: "About",
    cell: ({ row }) => (
      <RecordLink type={row.original.job.subjectType} id={row.original.job.subjectId} />
    ),
  }),
  failedJobColumn.display({
    id: "error",
    header: "Last error",
    cell: ({ row }) =>
      row.original.job.lastErrorCode === null ? (
        dash
      ) : (
        <>
          <code>{row.original.job.lastErrorCode}</code>
          {row.original.job.errorClass === null ? null : (
            <span className="text-xs text-muted-foreground">
              {" "}
              {errorClassLabels[row.original.job.errorClass]}
            </span>
          )}
        </>
      ),
  }),
  failedJobColumn.display({
    id: "run",
    header: "Last run",
    cell: ({ row }) =>
      row.original.job.lastRunRef === null ? dash : <code>{row.original.job.lastRunRef}</code>,
  }),
  failedJobColumn.display({
    id: "attempts",
    header: "Attempts",
    cell: ({ row }) => `${row.original.job.attempts} of ${row.original.job.maxAttempts}`,
  }),
  failedJobColumn.display({
    id: "action",
    header: "Action",
    cell: ({ row }) => (
      <Button type="button" variant="soft" size="sm" onClick={row.original.onRerun}>
        <Icon name="refresh" />
        Re-run{" "}
        <span className="sr-only">
          job {shortId(row.original.job.id)}: {taskLabels[row.original.job.taskId]}
        </span>
      </Button>
    ),
  }),
]);

type TaskFamilyView = Readonly<{
  taskId: string;
  label: string;
  counts: Readonly<{ PENDING: number; FAILED: number }>;
  oldestDueAgeSeconds: number | null | undefined;
  paused: boolean;
  ownPause: boolean;
  onPause: () => void;
  onResume: () => void;
}>;
const taskFamilyColumn = createColumnHelper<TaskFamilyView>();
const taskFamilyColumns = wrapCells([
  taskFamilyColumn.display({
    id: "task",
    header: "Task",
    meta: { rowHeader: true },
    cell: ({ row }) => <span className="font-semibold">{row.original.label}</span>,
  }),
  taskFamilyColumn.display({
    id: "waiting",
    header: "Waiting",
    meta: { className: "tabular-nums" },
    cell: ({ row }) => row.original.counts.PENDING,
  }),
  taskFamilyColumn.display({
    id: "failed",
    header: "Failed",
    meta: { className: "tabular-nums" },
    cell: ({ row }) => row.original.counts.FAILED,
  }),
  taskFamilyColumn.display({
    id: "oldest",
    header: "Oldest waiting",
    cell: ({ row }) => formatAge(row.original.oldestDueAgeSeconds),
  }),
  taskFamilyColumn.display({
    id: "status",
    header: "Status",
    cell: ({ row }) => <TaskStatusBadge paused={row.original.paused} />,
  }),
  taskFamilyColumn.display({
    id: "help",
    header: "Help",
    cell: ({ row }) =>
      row.original.taskId === "request.sla-check" ? (
        <RunbookLink to={runbooks.slaStorm}>SLA storm</RunbookLink>
      ) : (
        dash
      ),
  }),
  taskFamilyColumn.display({
    id: "action",
    header: "Action",
    cell: ({ row }) => {
      const { label, ownPause, paused, onPause, onResume } = row.original;
      return ownPause ? (
        <Button type="button" variant="soft" size="sm" onClick={onResume}>
          <Icon name="play" />
          Resume <span className="sr-only">{label}</span>
        </Button>
      ) : paused ? (
        <span className={hint}>Paused for every task</span>
      ) : (
        <Button type="button" variant="soft" size="sm" onClick={onPause}>
          <Icon name="pause" />
          Pause <span className="sr-only">{label}</span>
        </Button>
      );
    },
  }),
]);

/** 2. Failed jobs: re-run one job at a time. */
export function FailedJobsSection({
  failedJobs,
  capabilities,
  onOpen,
}: Readonly<{
  failedJobs: ReturnType<typeof useOperationsJobs>;
  capabilities: DiagnosticsCapabilities;
  onOpen: (dialog: DialogRequest) => void;
}>) {
  const jobs = failedJobs.data?.pages.flatMap((page) => page.items) ?? [];
  const failedJobViews: FailedJobView[] = jobs.map((job) => ({
    job,
    onRerun: () => onOpen({ kind: "requeue", jobId: job.id, taskLabel: taskLabels[job.taskId] }),
  }));

  return (
    <Section
      id="failed-jobs"
      titleId="diag-jobs"
      title="Failed jobs"
      runbookLinks={<RunbookLink to={runbooks.stuckJob}>Runbook: stuck job</RunbookLink>}
    >
      {failedJobs.isPending ? (
        <AsyncState
          kind="loading"
          title="Loading the failed jobs"
          message="Waiting for the answer."
        />
      ) : failedJobs.isError && failedJobs.data === undefined ? (
        <AsyncState
          kind="error"
          title="The failed jobs could not be read"
          message={describeDiagnosticsError(failedJobs.error).message}
          onRetry={() => void failedJobs.refetch()}
        />
      ) : jobs.length === 0 ? (
        <AsyncState kind="empty" title="No failed job" message="No job has used up its attempts." />
      ) : (
        <>
          <p className={hint} role="status">
            {jobs.length} failed {jobs.length === 1 ? "job" : "jobs"} shown, newest change first.{" "}
            {capabilities.manage ? "Re-run one job at a time." : "An administrator re-runs a job."}
          </p>
          <DataTable<FailedJobView>
            caption="Failed background jobs"
            data={failedJobViews}
            getRowId={(view) => view.job.id}
            rowAttributes={(view) => ({ "data-job": view.job.id })}
            columns={failedJobColumns}
            banded="strong"
            className="rounded-md"
            columnVisibility={{ action: capabilities.manage }}
          />
          {failedJobs.hasNextPage ? (
            <div>
              <Button
                type="button"
                variant="outline"
                disabled={failedJobs.isFetchingNextPage}
                onClick={() => void failedJobs.fetchNextPage()}
              >
                {failedJobs.isFetchingNextPage ? "Loading…" : "Load more failed jobs"}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}

/** 3. Task families: pause or resume one family, or every task. */
export function TaskFamiliesSection({
  summary,
  capabilities,
  onOpen,
}: Readonly<{
  summary: ReturnType<typeof useOperationsSummary>;
  capabilities: DiagnosticsCapabilities;
  onOpen: (dialog: DialogRequest) => void;
}>) {
  const allControl = summary.data?.controls.find((control) => control.taskId === "*");
  const allPaused = allControl?.paused === true;
  const taskFamilyViews: TaskFamilyView[] = (summary.data?.tasks ?? []).map((row) => {
    const label = taskLabels[row.taskId];
    const control = summary.data?.controls.find((entry) => entry.taskId === row.taskId);
    return {
      taskId: row.taskId,
      label,
      counts: row.counts,
      oldestDueAgeSeconds: row.oldestDueAgeSeconds,
      paused: row.paused,
      ownPause: control?.paused === true,
      onPause: () => onOpen({ kind: "control", taskId: row.taskId, label, pause: true }),
      onResume: () => onOpen({ kind: "control", taskId: row.taskId, label, pause: false }),
    };
  });

  return (
    <Section
      id="task-families"
      titleId="diag-tasks"
      title="Task families"
      runbookLinks={
        <>
          <RunbookLink to={runbooks.stuckJob}>Runbook: stuck job</RunbookLink>
          <RunbookLink to={runbooks.privacyCleanup}>Runbook: privacy cleanup failure</RunbookLink>
          <RunbookLink to={runbooks.staffCompromise}>
            Runbook: staff compromise and offboarding
          </RunbookLink>
        </>
      }
    >
      {summary.isPending ? (
        <AsyncState kind="loading" title="Loading the queues" message="Waiting for the answer." />
      ) : summary.isError ? (
        <AsyncState
          kind="error"
          title="The queues could not be read"
          message={describeDiagnosticsError(summary.error).message}
          onRetry={() => void summary.refetch()}
        />
      ) : (
        <>
          {capabilities.manage ? (
            <div>
              {allPaused ? (
                <Button
                  type="button"
                  variant="soft"
                  onClick={() =>
                    onOpen({ kind: "control", taskId: "*", label: "every task", pause: false })
                  }
                >
                  <Icon name="play" />
                  Resume every task
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() =>
                    onOpen({ kind: "control", taskId: "*", label: "every task", pause: true })
                  }
                >
                  <Icon name="pause" />
                  Pause every task
                </Button>
              )}
            </div>
          ) : (
            <p className={hint}>An administrator pauses and resumes a task family.</p>
          )}
          <DataTable<TaskFamilyView>
            caption="Queues of each task family"
            data={taskFamilyViews}
            getRowId={(view) => view.taskId}
            rowAttributes={(view) => ({ "data-task": view.taskId })}
            columns={taskFamilyColumns}
            banded="strong"
            className="rounded-md"
            columnVisibility={{ action: capabilities.manage }}
          />
        </>
      )}
    </Section>
  );
}
